import {
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useAgentStore } from "@/stores/agent-store";
import { Select } from "@/components/design/Select";
import { ModelConfigError } from "@/lib/model-config";
import {
  type CompanyGraph,
  DEFAULT_GRAPH,
  type CompanyGraphRun,
  type GraphRef,
  edgeKey,
  loadBuildableCompanies,
  loadCompanyGraph,
  loadCompanyGraphRuns,
  activateCompanyGraph,
  deleteCompanyGraph,
  loadCompanyGraphs,
  loadGraphBuild,
  startClustering,
} from "@/lib/company-graph";
import { collapse, collapsedKey, groupsIn, narrow } from "./company-graph-collapse";
import { foldCompanies, groupName } from "./company-graph-companies";
import { type Share, DEFAULT_SIDE_WIDTH, clampSideWidth, resizePanes, shareOf } from "./company-graph-width";
import {
  COMPANY_LABEL,
  LAYOUTS,
  ORDERS,
  type LayoutKind,
  type Order,
  PER_CHAR,
  type PlacedEdge,
  PLAIN,
  RELATION_COLOUR,
  SOURCES,
  colourOf,
  layout,
  markPoints,
  shortName,
} from "./company-graph-layout";

// WHAT THE QUESTION WALKED, which is the one thing an answer cannot show. "Four companies" reads
// identically whether the verb found its relation or matched every relation to the thing, and that
// difference is the whole claim of the graph route. So the graph is drawn, and the edges one
// question used are lit.
//
// RADIAL, AND DETERMINISTIC. Things on an outer ring grouped by what they are, companies on an
// inner ring beside the things they touch, edges as chords through the middle. A force simulation
// would draw a different picture every load, and a reader comparing two searches would then be
// comparing two drawings.

const timeOf = (iso: string) => {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? "" : at.toLocaleTimeString();
};

/** The node radius grows with how many edges touch it, so the hubs read as hubs. */
const radiusOf = (degree: number) => 4.5 + Math.min(7, degree * 1.1);

/** The band along a group's top edge that its own name is written in. */
const TITLE = 16;

/**
 * How much one zoom step changes the picture, and how far it may go either way.
 *
 * A half again per step: big enough that one click is worth making, small enough that a reader
 * can stop where they meant to. The bounds exist because a picture that can be zoomed to nothing
 * can be lost, and one that can be zoomed forever can be scrolled off its own edge.
 */
/**
 * One sidebar section's header.
 *
 * FOUR PANELS USED TO CARRY FOUR BOXES, four borders and four full-size bars, and the lists under
 * them got six rows apiece. One rule and one small line each gives the lists the space back, and
 * the count says how long a list is without scrolling it — "12/200" while a search narrows it.
 */
function PanelHead({ children, shown, total }: { children: ReactNode; shown: number; total?: number }) {
  return (
    <h2 className="border-subtle flex items-baseline gap-2 border-b px-3 py-2 text-smycles-caption uppercase tracking-[0.16em] text-ink-muted">
      <span className="min-w-0 flex-1 truncate">{children}</span>
      <span className="shrink-0 tabular-nums opacity-60">{total != null && shown !== total ? `${shown}/${total}` : shown}</span>
    </h2>
  );
}

/** The two search boxes, which are the same box. Ghost rather than outlined: the list below is the
 *  content, and a hard border round the field made the panel read as a form. */
const FIND = "w-full rounded-md bg-ink/[0.05] px-2.5 py-1.5 text-smycles-body placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-ink-ui";
/** One row in any of the three lists, so a company, a thing and a relation sit on the same grid. */
const ROW = "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 transition hover:bg-ink/[0.05]";

const STEP = 1.5;
const CLOSEST = 8;

/**
 * How far out a reader may go when the picture already fits, and the floor under everything.
 *
 * A QUARTER IS NOT "OUT" FOR A RING NINE THOUSAND PIXELS ACROSS — a quarter of it is still two
 * and a half times the window, so the furthest the control would go was a view still cut off on
 * every side. The real floor is whatever shows the whole thing; this one only applies when that
 * is already more than a quarter, and the hard one exists so a box measured at nothing cannot
 * scale the picture to nothing.
 */
const FURTHEST = 0.25;
const HARD_FLOOR = 0.02;

/** How often a running grouping job is asked about — the same beat the build page uses. */
const POLL_MS = 2000;
/**
 * How long a grouping run may be followed before this page gives up on it.
 *
 * THE LOOP HAD NO WAY OUT. A job row that stays on `running` — a pod restarted mid-run, say — left
 * the tab asking every two seconds for as long as it was open, including long after the reader had
 * navigated away. Generous rather than tight: a level over a large graph is a few dozen model
 * calls, and giving up on one that is still working costs the reader the re-read.
 */
const GROUPING_MAX_MS = 15 * 60 * 1000;

export default function CompanyGraphPage() {
  const intl = useIntl();
  // A TURN ENDING IS THE SIGNAL, not a timer. The drawer sits in this same tree, so `pending`
  // falling is exactly "a question just finished" — and a question that took the label route
  // simply adds no run, which is a list that does not change rather than a wasted poll.
  const pending = useAgentStore((s) => s.pending);
  // WHICH GRAPH THIS PAGE IS LOOKING AT — a view setting and nothing else. Questions all go to
  // the one graph an admin made active, so picking another here reads it without answering from
  // it. Null is "whichever the server calls active", so the page opens on what readers get.
  // SEEDED FROM THE URL, ONCE. `?id=` is how the build page hands over the graph it just made —
  // without it the only way to reach a graph that is not the active one is to find it in the
  // picker, and the reader who just built it is the one person who knows which of twelve names is
  // theirs. Read at mount and never again: after that the picker owns this, and a param that kept
  // being re-read would drag the reader back to the built graph on every change.
  const [params] = useSearchParams();
  const [viewing, setViewing] = useState<string | null>(params.get("id"));
  const [activating, setActivating] = useState(false);
  const [catalogue, setCatalogue] = useState<GraphRef[]>([]);
  const navigate = useNavigate();
  // THE GRAPH STORES NAMES, the company page needs an id. Read from the same endpoint the builder
  // resolves against, so a node that names a company really is the company it opens.
  const [idOf, setIdOf] = useState<ReadonlyMap<string, string>>(new Map());
  const [graph, setGraph] = useState<CompanyGraph | null>(null);
  const [all, setRuns] = useState<CompanyGraphRun[]>([]);
  /** A recorded walk, told apart from the others: when it was asked, and what was asked. */
  const keyOf = (entry: CompanyGraphRun) => `${entry.at}\u0000${entry.question}`;
  /**
   * WHICH RECORDED WALK IS DRAWN, by what it is rather than where it sits.
   *
   * This was an index into `runs`, and `runs` is re-read newest-first after every turn and
   * filtered to the graph on screen — so the next question slid a different walk under the
   * highlight, and so did switching graph. [keyOf] is the pair that identifies one: when it was
   * asked, and what was asked.
   */
  const [selected, setSelected] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  // A VIEW SETTING, not part of the search: which arrangement a reader finds legible says nothing
  // about which graph their questions go to, so it stays here rather than in the agent store.
  const [kind, setKind] = useState<LayoutKind>(LAYOUTS[0]!);
  // A VIEW SETTING like the layout: which way a reader wants the picture arranged says nothing
  // about which graph their questions go to.
  const [order, setOrder] = useState<Order>(ORDERS[0]!);
  // WHICH COMPANIES ARE DRAWN, and nothing else: the graph that answers questions is the whole
  // one. Empty means all of them, so the filter costs nothing until a reader uses it.
  const [only, setOnly] = useState<ReadonlySet<string>>(new Set());
  const [onlyThings, setOnlyThings] = useState<ReadonlySet<string>>(new Set());
  // The third filter, and READONLY like its two siblings: a mutating `.add` on one of these
  // compiles and silently skips the re-render, so the type is what stops it. It sat up among the
  // hover state because it took the old `muted`'s slot, which was transient highlight and not a
  // filter at all.
  const [onlyRelations, setOnlyRelations] = useState<ReadonlySet<string>>(new Set());
  // THE FOURTH, and the only one about where a thing CAME FROM rather than what it is. Same shape
  // as its siblings for the same reason.
  const [onlySources, setOnlySources] = useState<ReadonlySet<string>>(new Set());
  const [search, setSearch] = useState("");
  const [searchThings, setSearchThings] = useState("");
  // Which groups a reader has opened. Folded is the default, because the whole reason a group
  // exists is that a hundred names at once is not a picture.
  const [opened, setOpened] = useState<ReadonlySet<string>>(new Set());
  const [grouping, setGrouping] = useState(false);
  // HOW COARSE THE PICTURE IS: 0 the bare nodes, 1 their groups, 2 the groups of those. Held
  // against the graph it was chosen for — switching
  // graphs must fall back to the new one's own top level without writing state from an effect.
  const [chosen, setChosen] = useState<{ for: string | null; level: number } | null>(null);
  // Which graph the reader has been asked about, not a bare flag: switching graphs with the
  // question open would otherwise leave it standing over a different name.
  const [asking, setAsking] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  /**
   * AN ADMIN ACTION THAT DID NOT WORK, said out loud.
   *
   * Activate, delete and the catalogue read used to swallow their failures: on a 5xx the button
   * simply did nothing, which is indistinguishable from an action still in flight — and it is how
   * the deleted-graph case below used to present itself. In this page's OWN words rather than the
   * server's: `failed`, `job.stage` and `job.warning` are English strings written for a log, and a
   * German UI printing them raw tells the reader less than one sentence of its own.
   */
  const [problem, setProblem] = useState<"activate" | "delete" | "list" | "grouping" | null>(null);
  /**
   * Bumped to re-read the graph when the ID DID NOT CHANGE.
   *
   * A delete falls back to whatever is left, and with nothing explicitly chosen that is `null` over
   * `null` — React sees no change, the effect below does not re-run, and the graph that was just
   * deleted stays drawn, still offering "Make active".
   */
  const [reloads, setReloads] = useState(0);
  /** False once this page is gone, so a job still being followed stops being followed. */
  const mounted = useRef(true);
  // HOW BIG THE PICTURE IS DRAWN, not what it holds. At seventeen hundred things a label is a few
  // pixels of a very large canvas, and the way to read one is to come closer to it.
  const [zoom, setZoom] = useState(1);
  const canvas = useRef<HTMLDivElement | null>(null);
  // THE BOX'S SIZE AS STATE, because the floor of the zoom is a property of the window and the
  // control has to be drawn against it. A ref cannot be read while rendering, and measuring in an
  // effect is the cascading-render shape — a callback ref measures exactly when the node arrives.
  const [box, setBox] = useState<{ width: number; height: number } | null>(null);
  /**
   * What the next zoom has to keep under the pointer, applied once the canvas has its new size.
   *
   * NOT IN A `requestAnimationFrame`. One scheduled from the click can run before React has
   * committed the bigger canvas, and the browser then clamps the scroll to the size it still has
   * — at the fit zoom that maximum is zero, so the correction became "stay at the top left" and
   * the picture grew away from the click instead of into it.
   */
  const anchor = useRef<{ into: { x: number; y: number }; at: { x: number; y: number }; grew: number } | null>(null);
  const attach = useCallback((node: HTMLDivElement | null) => {
    canvas.current = node;
    if (!node) return undefined;
    const read = () => setBox({ width: node.clientWidth, height: node.clientHeight });
    read();
    const watching = new ResizeObserver(read);
    watching.observe(node);
    return () => watching.disconnect();
  }, []);
  // THAT one did, which is all there is to know: the error replaces the page, picker included,
  // so nothing can ask for another graph until the reader reloads — and a reload opens on the
  // active one. Which graph failed mattered while a switch could outlive the failure.
  const [failed, setFailed] = useState(false);
  // THE CATALOGUE WAS READ, as opposed to not yet or not at all. It is what separates the two ways
  // the drawing can be missing: a service that cannot be reached fails BOTH calls, while an
  // instance that simply holds no graph answers this one with an empty list.
  const [listed, setListed] = useState(false);
  // HOW WIDE THE FILTER PANEL IS. Three hundred fits a count and an ellipsis, which is what every
  // company row was: the names on this page are legal entities, and the things are sentences.
  const [sideWidth, setSideWidth] = useState(DEFAULT_SIDE_WIDTH);
  const dragFrom = useRef<{ x: number; w: number } | null>(null);
  // HOW THE THREE LISTS SHARE THE COLUMN. Equal thirds until a reader says otherwise, which is the
  // wrong default often enough to be worth dragging: an instance with no recorded searches gives a
  // third of the sidebar to a sentence saying so, while the things list has four thousand rows.
  // Null for the relations and the sources: a row per predicate and a row per input, both fixed by
  // vocabularies rather than by the corpus, so their own height is the right one until a reader
  // says otherwise. Each joins the split on the first drag of the rule above it.
  const [shares, setShares] = useState<readonly Share[]>([1, 1, 1, null, null]);
  const panes = useRef<(HTMLElement | null)[]>([]);
  const dragPane = useRef<{ y: number; a: number; b: number; at: number } | null>(null);
  // HOW COARSE THE COMPANY SIDE IS DRAWN, and nothing to do with [level]: that one is the search's
  // cluster layer, built on the server a few dozen model calls at a time. This is arithmetic over
  // the edges already in hand, so it changes the picture at once and never changes the graph.
  const [buckets, setBuckets] = useState(0);
  /** The company buckets a reader has opened, kept apart from [opened] because they are not groups
   *  of the same thing: one is the graph's own layer, the other is how this page chose to draw. */
  const [openedCompanies, setOpenedCompanies] = useState<ReadonlySet<string>>(new Set());
  // NOTHING TO DRAW, as opposed to nothing answering. The service says 404 for both "no graph is
  // active" and "no graph exists", and neither is an outage: an instance holds none until one is
  // built, and deleting the active one leaves the rest sitting there unselected. Told apart from
  // [failed] because that one replaces the page, picker included, which in this state strands a
  // reader among graphs they can see in the list and cannot open.
  const [missing, setMissing] = useState(false);

  // The graph is a fixture baked into the agent-service image, so it cannot change while the page
  // is open and is read once.
  // What there is to choose from. Read once: a graph appears when one is built, which is a
  // navigation away and back.
  useEffect(() => () => {
    mounted.current = false;
  }, []);

  useEffect(() => {
    let alive = true;
    loadCompanyGraphs().then(
      (it) => {
        if (!alive) return;
        setCatalogue(it);
        setListed(true);
      },
      () => {
        if (!alive) return;
        setCatalogue([]);
        setProblem("list");
      },
    );
    loadBuildableCompanies().then(
      (it) => alive && setIdOf(new Map(it.map((c) => [c.name, c.id]))),
      // A failure here costs the double-click and nothing else: the graph is still a graph.
      () => {},
    );
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    loadCompanyGraph(viewing).then(
      (it) => {
        if (!alive) return;
        setGraph(it);
        // WHAT THE LAST ASK SAID, not what an earlier one did: picking a graph out of the panel
        // below is how a reader leaves this state, and a flag left standing draws that panel over
        // the graph they just chose.
        setMissing(false);
      },
      (reason) => {
        if (!alive) return;
        const nothingToDraw = reason instanceof ModelConfigError && reason.status === 404;
        setMissing(nothingToDraw);
        setFailed(!nothingToDraw);
      },
    );
    return () => {
      alive = false;
    };
  }, [viewing, reloads]);

  // The runs are the reader's own, across every conversation they have had — a reader comparing
  // two questions usually asked them in two of them. Re-read whenever a turn finishes: on mount
  // `pending` is already false, so this is also the first read.
  useEffect(() => {
    if (pending) return;
    let alive = true;
    loadCompanyGraphRuns().then(
      (it) => alive && setRuns(it),
      () => {},
    );
    return () => {
      alive = false;
    };
  }, [pending]);
  // FOLDED BEFORE PLACED, which is why no layout knows what a group is: they place whatever graph
  // they are handed, and this hands them one with the groups already folded in.
  // The coarsest the graph can be drawn, which is where a reader starts: the whole reason a level
  // was built is that the level under it is too many names at once.
  const levels = useMemo(
    () => (graph?.clusters ?? []).reduce((most, it) => Math.max(most, it.level ?? 1), 0),
    [graph],
  );
  const level = chosen != null && chosen.for === (graph?.id ?? null) ? chosen.level : levels;
  const setLevel = (it: number) => setChosen({ for: graph?.id ?? null, level: it });
  // NARROWED BEFORE FOLDED, so the groups form over what is actually drawn: a group whose members
  // are all reached by companies that were filtered out is not a group of this picture.
  const shown = useMemo(
    () => (graph ? narrow(graph, only, onlyThings, onlyRelations, onlySources) : null),
    [graph, only, onlyThings, onlyRelations, onlySources],
  );
  const collapsed = useMemo(() => (shown ? collapse(shown, opened, level) : null), [shown, opened, level]);
  // FOLDED AFTER THE THINGS, over what is actually drawn: a bucket is named after the thing most of
  // its companies reach, and at a coarse level that thing is a group rather than a leaf.
  const folded = useMemo(
    () =>
      collapsed
        ? foldCompanies(collapsed, buckets, openedCompanies, {
            other: intl.formatMessage({ id: "companyGraph.otherCompanies", defaultMessage: "the rest" }),
            none: intl.formatMessage({ id: "companyGraph.noRelations", defaultMessage: "no relations" }),
          })
        : null,
    [collapsed, buckets, openedCompanies, intl],
  );
  const placed = useMemo(
    () => (folded ? layout(folded.graph, kind, folded.enclosing, order) : null),
    [folded, kind, order],
  );

  /**
   * Starts a grouping run and waits for it, because it is a JOB and not a call that returns the
   * answer — reading the graph back before it has finished reads the graph it had before.
   */
  const group = async () => {
    setGrouping(true);
    setProblem(null);
    try {
      const started = await startClustering(graph?.id ?? DEFAULT_GRAPH);
      let job = started;
      const until = Date.now() + GROUPING_MAX_MS;
      while (job.status === "running") {
        // GONE, OR GIVEN UP ON. Neither is a failure of the run — it carries on upstream and the
        // graph will hold whatever it wrote — so this stops watching rather than reporting a
        // result it does not have.
        if (!mounted.current) return null;
        if (Date.now() > until) {
          setProblem("grouping");
          return null;
        }
        await new Promise((wake) => window.setTimeout(wake, POLL_MS));
        // AND AGAIN AFTER THE WAIT, which is where the page is actually lost: the check above runs
        // before a two-second sleep, so without this one more request goes out after the unmount.
        if (!mounted.current) return null;
        job = await loadGraphBuild(started.jobId);
      }
      if (!mounted.current) return null;
      // Re-read even when it failed: a partial write is still what the server now holds, and the
      // drawing must show what is there rather than what was asked for.
      const next = await loadCompanyGraph(viewing);
      setGraph(next);
      return next;
    } catch {
      // Grouping is an extra pass over a graph that is already drawn; losing it costs the pass.
      return null;
    } finally {
      setGrouping(false);
    }
  };

  /**
   * One level coarser — shown straight away when the graph already holds it, built when it does not.
   *
   * ASKED FOR ONCE. A level is a few dozen model calls, so climbing back up to one already built
   * must not pay for it again; and the level is then read back off the graph rather than assumed,
   * so a run that produced nothing leaves the control where it was instead of one step ahead of
   * the picture.
   */
  /**
   * Removes the graph being shown, then falls back to the default.
   *
   * THE CATALOGUE IS RE-READ RATHER THAN EDITED IN PLACE. The service decides what is left — a
   * delete it refused, or one that took a file with it — and a list patched here would be this
   * page's opinion of that.
   */
  const remove = async () => {
    const id = graph?.id;
    if (!id) return;
    setDeleting(true);
    setProblem(null);
    try {
      await deleteCompanyGraph(id);
      const left = await loadCompanyGraphs();
      setCatalogue(left);
      setOpened(new Set());
      setViewing(left.find((it) => it.id !== id)?.id ?? null);
      // ALWAYS RE-READ, because the id may not have changed: with nothing explicitly chosen the
      // fallback is `null`, which is what `viewing` already was.
      setReloads((it) => it + 1);
    } catch {
      // Nothing was removed, so there is nothing to put back; the question closes and the graph
      // is still there to try again on — now with a line saying the delete did not happen.
      setProblem("delete");
    } finally {
      setDeleting(false);
      setAsking(null);
    }
  };

  /**
   * Makes the graph being shown the one every question is answered from, for everybody.
   *
   * The catalogue is re-read rather than edited here for the same reason the delete re-reads it:
   * the server decides which graph is active, and a flag flipped here would be this page's
   * opinion of that.
   */
  const activate = async () => {
    const id = graph?.id;
    if (!id) return;
    setActivating(true);
    setProblem(null);
    try {
      await activateCompanyGraph(id);
      setCatalogue(await loadCompanyGraphs());
    } catch {
      // Nothing changed upstream, so the line still says which graph is active and the button is
      // still there to try again — beside a line saying this attempt did not.
      setProblem("activate");
    } finally {
      setActivating(false);
    }
  };

  /** The scale at which the whole picture fits the box, or null before the box has a size. */
  const fitScale = () => {
    if (!box || !placed || box.width === 0 || box.height === 0) return null;
    return Math.min(box.width / placed.width, box.height / placed.height);
  };

  /** The furthest out this picture is worth going: what fits, or a quarter when that is nearer. */
  const furthest = () => {
    const fit = fitScale();
    return fit == null ? FURTHEST : Math.max(HARD_FLOOR, Math.min(FURTHEST, fit));
  };

  /**
   * Zooms about a point on the screen, keeping what is under it where it is.
   *
   * Without the scroll correction the canvas grows from its top-left corner and whatever a reader
   * was looking at slides away from them — on a canvas nine thousand pixels across it leaves the
   * screen entirely, and the click reads as having lost the picture rather than entered it.
   */
  const zoomAbout = (clientX: number, clientY: number, factor: number) => {
    const after = Math.min(CLOSEST, Math.max(furthest(), zoom * factor));
    if (after === zoom) return;
    const box = canvas.current;
    if (box) {
      // Where the pointer is INSIDE the scrolling box, and what sits under it in the canvas's own
      // coordinates. `clientLeft` is the border, which is outside the scrolled area.
      const rect = box.getBoundingClientRect();
      const into = { x: clientX - rect.left - box.clientLeft, y: clientY - rect.top - box.clientTop };
      anchor.current = {
        into,
        at: { x: box.scrollLeft + into.x, y: box.scrollTop + into.y },
        grew: after / zoom,
      };
    }
    setZoom(after);
  };

  // THE SAME GRIP THE ASSISTANT DRAWER USES, down to the direction: the panel is anchored right,
  // so dragging LEFT grows it. Pointer capture keeps the drag alive over the canvas, which is the
  // one place the pointer is certain to cross on the way.
  const onGripDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragFrom.current = { x: e.clientX, w: sideWidth };
  }, [sideWidth]);
  const onGripMove = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const from = dragFrom.current;
    if (!from) return;
    setSideWidth(clampSideWidth(from.w + (from.x - e.clientX), window.innerWidth));
  }, []);
  const onGripUp = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    dragFrom.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  }, []);

  // Measured off the panes themselves rather than tracked: the column is a flex box answering to
  // the window, so what a pane is worth in pixels is only knowable at the moment of the grab.
  const onPaneDown = useCallback((at: number) => (e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const a = panes.current[at]?.getBoundingClientRect().height ?? 0;
    const b = panes.current[at + 1]?.getBoundingClientRect().height ?? 0;
    // A pane below that has never been dragged has no weight yet. Seeded from what it is worth on
    // screen right now, so taking hold of the rule moves nothing by itself.
    setShares((before) =>
      before[at + 1] == null ? before.map((w, i) => (i === at + 1 ? shareOf(before[at] ?? 1, a, b) : w)) : before,
    );
    dragPane.current = { y: e.clientY, a, b, at };
  }, []);
  const onPaneMove = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const from = dragPane.current;
    if (!from) return;
    const moved = e.clientY - from.y;
    setShares((before) => resizePanes(before, from.at, from.a + moved, from.b - moved) ?? before);
  }, []);
  const onPaneUp = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    dragPane.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  }, []);

  /** The same from the header, about the middle of what is on screen. */
  const zoomBy = (factor: number) => {
    const rect = canvas.current?.getBoundingClientRect();
    zoomAbout(
      (rect?.left ?? 0) + (rect?.width ?? 0) / 2,
      (rect?.top ?? 0) + (rect?.height ?? 0) / 2,
      factor,
    );
  };

  // AFTER THE COMMIT, synchronously, while the canvas already has its new size and before the
  // browser paints — so there is nothing to clamp against and nothing to flicker.
  useLayoutEffect(() => {
    const keep = anchor.current;
    anchor.current = null;
    const area = canvas.current;
    if (!keep || !area) return;
    area.scrollLeft = keep.at.x * keep.grew - keep.into.x;
    area.scrollTop = keep.at.y * keep.grew - keep.into.y;
  }, [zoom]);

  const deeper = async () => {
    if (level < levels) {
      setLevel(level + 1);
      return;
    }
    const next = await group();
    if (next) setLevel((next.clusters ?? []).reduce((most, it) => Math.max(most, it.level ?? 1), 0));
  };
  // ONLY THIS GRAPH'S WALKS. A run recorded against another one highlights nothing here, which
  // reads as a broken highlight rather than as a run that belongs somewhere else. A run from
  // before graphs were named has no id and is shown on the default, where it was made.
  const runs = useMemo(
    () => all.filter((run) => !run.graphId || !graph?.id || run.graphId === graph.id),
    [all, graph],
  );
  // THE RUN ITSELF, found again by what it IS. `selected` used to be an index into `runs`, and
  // `runs` is re-read newest-first after every turn and filtered per graph — so the next turn
  // slid a different walk under the highlight, and so did switching graph. A run nobody can
  // find any more simply stops being selected.
  const run = selected === null ? null : (runs.find((it) => keyOf(it) === selected) ?? null);

  // The highlight, as sets read once per render rather than a scan per drawn element.
  const fromRun = useMemo(() => {
    if (!run) return null;
    return {
      edges: new Set(run.edges.map((e) => edgeKey(e.company, e.predicate, e.obj))),
      nodes: new Set([...run.edges.map((e) => e.company), ...run.edges.map((e) => e.obj)]),
    };
  }, [run]);

  // Hovering a node reads its neighbourhood — the second way to interrogate the picture, and the
  // one that works before any question has been asked.
  const fromHover = useMemo(() => {
    if (!hovered || !placed || !folded) return null;
    // A HOVERED GROUP IS ITS MEMBERS. Folded, the group is a node and hovering it lights every
    // company that reaches it; opened, it is only a box, and without this the reading the fold
    // gave you is gone — you can see one member's edges at a time and never the group's.
    const ids = new Set(folded.inside.get(hovered) ?? [hovered]);
    const touching = placed.edges.filter((e) => ids.has(e.from.id) || ids.has(e.to.id));
    return {
      edges: new Set(touching.map((e) => e.id)),
      nodes: new Set([...ids, ...touching.flatMap((e) => [e.from.id, e.to.id])]),
    };
  }, [hovered, placed, folded]);

  // NO GRAPH IS NOT A FAILURE. An instance holds none until one is built — the shipped fixture can
  // be deleted, and a fresh database has nothing else — and the service answers that with a 404
  // like any other missing graph. Reported as "the service is unavailable" it sent a reader looking
  // for an outage; what is actually needed from here is the builder.
  //
  // AND THERE MAY BE GRAPHS ANYWAY. Only the ACTIVE one answers an unnamed ask, so deleting it
  // leaves every other graph stored, listed and unreachable: the page took the error branch, which
  // replaces the picker, so the one control that could have opened any of them was gone. The list
  // is read by its own call and survives this, so it is what this state offers.
  if (missing) {
    const pickable = listed && catalogue.length > 0;
    return (
      <div className="flex flex-col items-start gap-2 p-6 text-smycles-body text-ink-muted">
        {pickable ? (
          <FormattedMessage
            id="companyGraph.inactive"
            defaultMessage="No graph is active, so there is nothing to draw. Pick one to look at."
          />
        ) : (
          <FormattedMessage
            id="companyGraph.none"
            defaultMessage="There is no company graph yet. Build one and it is drawn here."
          />
        )}
        {pickable && (
          <label className="flex items-center gap-2">
            <FormattedMessage id="companyGraph.showing" defaultMessage="Showing" />
            {/* THE EMPTY OPTION STAYS, unlike on the drawn page where a graph is always being
                shown. Nothing is selected here, and a native select with no option for that
                displays its FIRST one anyway: the panel read as though a graph were open, and
                choosing that same graph changed nothing, so the one way out of this state was
                shut for whichever graph the browser happened to show. */}
            <Select
              id="company-graph-pick-inactive"
              variant="pill"
              value={viewing ?? ""}
              onChange={(id) => setViewing(id)}
              options={catalogue.map((it) => ({ value: it.id, label: it.name }))}
            />
          </label>
        )}
        <Link to="/admin/company-graph/build" className="underline">
          <FormattedMessage id="companyGraph.build" defaultMessage="Build one…" />
        </Link>
      </div>
    );
  }
  if (failed) {
    return (
      <div className="p-6 text-smycles-body text-ink-muted">
        <FormattedMessage
          id="companyGraph.failed"
          defaultMessage="The company graph could not be loaded. It ships with the agent-service, so this usually means that service is unavailable."
        />
      </div>
    );
  }
  if (!graph || !placed || !folded || !shown) {
    return (
      <div className="p-6 text-smycles-body text-ink-muted">
        <FormattedMessage id="companyGraph.loading" defaultMessage="Loading the company graph…" />
      </div>
    );
  }

  // WHAT AN OPENED GROUP IS ABOUT. Opening one asks a question about it, and the rest of the
  // picture is the context that question sits in — so it stays drawn and goes quiet, rather than
  // disappearing (which loses the reader's place) or staying loud (which buries the answer).
  const inGroup = folded.inside.size === 0 ? null : new Set([...folded.inside.values()].flat());

  // Only a graph this instance can really remove, and only while the question is about THIS one.
  // WHAT THE THINGS FILTER OFFERS: the groups, then the things. Read by the same rules the search
  // reads them by, so a group the loader would refuse never appears in a list to pick from.
  const pickable = [
    ...groupsIn(graph).map((it) => ({ label: it.label, covers: it.members.length })),
    ...graph.nodes.map((it) => ({ label: it.label, covers: null as number | null })),
  ];

  // WHAT THE RELATION FILTER OFFERS: every predicate the graph holds, in the GRAPH's own order.
  // It used to be the colour key's order, which silently meant the colour key's SET: the ninth
  // predicate arrived, forty-five of a hundred and thirty-six edges were drawn in the
  // unknown-predicate grey, and the panel a reader consults to find out what the graph holds did
  // not list them at all. The graph names its predicates; this lists what it names.
  const held = new Set(graph.edges.map((it) => it.predicate));
  const relationsIn = [
    ...graph.predicates.map((it) => it.name),
    ...Object.keys(RELATION_COLOUR),
  ].filter((it, at, all) => held.has(it) && all.indexOf(it) === at);
  // COUNTED OVER THE SAME GRAPH THE LIST COMES FROM, which is the whole one. It used to be summed
  // over the DRAWN edges, and once the list stopped being drawn-only the two could disagree: ticking
  // one relation left every other row reading "0" — true of the picture and false of the graph, in
  // the one panel a reader consults to find out what the graph holds. Unfiltered the number is
  // unchanged, because a drawn edge's weight is exactly the graph edges folded into it.
  const relationCounts = new Map<string, number>();
  for (const edge of graph.edges) relationCounts.set(edge.predicate, (relationCounts.get(edge.predicate) ?? 0) + 1);
  // WHICH INPUT NAMED EACH THING, read off the whole graph for the same reason the relation counts
  // are: the panel says what the graph holds, not what survived the filter. A node written before
  // the sources existed is a summary node — see `GraphNode.source`.
  const sourceOf = new Map(graph.nodes.map((it) => [it.label, it.source ?? "summary"]));
  const sourceCounts = new Map<string, number>();
  for (const source of sourceOf.values()) sourceCounts.set(source, (sourceCounts.get(source) ?? 0) + 1);
  // A SOURCE WITH NOTHING IN IT IS NOT OFFERED. An instance that never built with the Akte would
  // otherwise carry a permanent third row reading "0", and a reader who may not see record nodes
  // would be shown a filter for what they are not served.
  const sourcesIn = SOURCES.filter((it) => (sourceCounts.get(it) ?? 0) > 0);
  // THE RELATION IN THE READER'S LANGUAGE. The graph carries `de` and `en` for every predicate it
  // holds, and this rendered the raw enum — tolerable on a legend chip, wrong once the chip became
  // the accessible name of a checkbox in a German UI. Falls back to the enum for a predicate the
  // graph did not name, which is what `colourOf` does for one outside the key.
  // THE SOURCE IN THE READER'S LANGUAGE. Unlike the relations, the graph does NOT carry a wording
  // for these: `summary | label | record` are the service's own words for its three inputs, and
  // the reader's words for them are a UI concern — "Organisationsakte" is what the thing is called
  // in spark, not what the document calls it.
  const sourceName = (source: string): string =>
    source === "label"
      ? intl.formatMessage({ id: "companyGraph.sourceLabel", defaultMessage: "Expertise label" })
      : source === "record"
        ? intl.formatMessage({ id: "companyGraph.sourceRecord", defaultMessage: "Organisational record" })
        : intl.formatMessage({ id: "companyGraph.sourceSummary", defaultMessage: "Self-description" });
  const relationName = (predicate: string): string => {
    const named = graph.predicates.find((it) => it.name === predicate);
    return (intl.locale.startsWith("de") ? named?.de : named?.en) ?? predicate.toLowerCase();
  };

  // EVERY GRAPH CAN GO. One used to ship inside the service and be rewritten at every start, so
  // the server refused to delete it and this hid the control rather than offering something that
  // would be turned down. Nothing ships now: a graph exists because someone built it.
  // HOISTED so a header can say how much of its list is on screen. Filtering inside the map left
  // the count and the rows reading different numbers the moment a search narrowed one of them.
  const companiesShown = graph.companies.filter((it) => it.toLowerCase().includes(search.trim().toLowerCase()));
  const thingsShown = pickable.filter((it) => it.label.toLowerCase().includes(searchThings.trim().toLowerCase()));

  const deletable = catalogue.some((it) => it.id === graph.id);
  // READ FROM THE CATALOGUE, not from what was clicked: the server owns which graph is active, so
  // a failed activation leaves the line saying what is true rather than what was asked for.
  const isActive = catalogue.some((it) => it.id === graph.id && it.active);
  const asked = asking === graph.id;

  // ONE PASS OVER THE EDGES, NOT ONE PER NODE.
  //
  // `nodeLit` used to answer by scanning every edge and asking `isLit` about each — so a picture of
  // three thousand nodes over four thousand edges cost fourteen MILLION of those calls per render,
  // each building a key and scanning a group's members. Clicking a recorded search locked the tab:
  // the highlight was correct and the click that would have cleared it was never processed, which
  // is why it read as a freeze with no way back rather than as something slow.
  //
  // The same answers, collected once. Hover keeps its own sets, which were always built this way.
  const lit = ((): { edges: Set<string>; nodes: Set<string> } | null => {
    if (fromHover || (!inGroup && !fromRun)) return null;
    const edges = new Set<string>();
    const nodes = new Set<string>();
    for (const edge of placed.edges) {
      const on = fromRun
        ? fromRun.edges.has(edgeKey(edge.from.id, edge.predicate, edge.to.id)) ||
          // A WALK IS RECORDED AGAINST THE LEAVES IT WALKED. Folded, the group's edge matches none
          // of them, and a reader clicking their own search would see nothing light up at all.
          (folded?.rolled.get(collapsedKey(edge.from.id, edge.predicate, edge.to.id)) ?? []).some((m) =>
            fromRun.edges.has(edgeKey(m.company, m.predicate, m.object)),
          )
        : inGroup!.has(edge.to.id);
      if (!on) continue;
      edges.add(edge.id);
      // WHICH END COUNTS differs, and always did: an opened group lights what reaches INTO it,
      // while a recorded walk lights both ends of every edge it took.
      nodes.add(edge.from.id);
      if (fromRun) nodes.add(edge.to.id);
    }
    return { edges, nodes };
  })();

  const isLit = (edge: PlacedEdge): boolean => {
    if (fromHover) return fromHover.edges.has(edge.id);
    if (lit) return lit.edges.has(edge.id);
    return true;
  };
  const nodeLit = (id: string): boolean => {
    if (fromHover) return fromHover.nodes.has(id);
    if (inGroup && !fromRun) return inGroup.has(id) || (lit?.nodes.has(id) ?? false);
    if (fromRun) return fromRun.nodes.has(id) || (lit?.nodes.has(id) ?? false);
    return true;
  };
  const dimmed = Boolean(fromHover || fromRun);

  // HOW CROWDED THE BUNDLE IS, and therefore how an edge at rest should be drawn.
  //
  // A chord at 0.8 over a 1.5px line is one readable relation. Two thousand of them composite into
  // an opaque block with the labels floating either side of it, which is what a hundred-company
  // graph actually looks like — the picture stops being a drawing and becomes a wall.
  //
  // Faint and thin, the same two thousand read as TONE: where the graph is dense the band is dark,
  // where it is thin you can see through it, and a lit edge at 0.86 comes off the surface instead
  // of competing with everything behind it. Square-rooted so a graph twice the size is not drawn
  // half as faint — doubling the chords roughly doubles what any one pixel accumulates.
  const crowd = Math.sqrt(Math.max(1, placed.edges.length / 140));
  const atRest = Math.max(0.1, 0.8 / crowd);
  // COLOUR IS INFORMATION UNTIL IT IS AVERAGE. Eight hues laid over each other at a fifth opacity
  // do not read as eight relations, they read as the brown you get from mixing them — so past the
  // point where a chord is more overlap than line, the resting bundle is one neutral and the hues
  // come back the moment a question or a hover picks a few edges out of it.
  const washed = crowd >= 1.6;

  // THE BOX STAYS WHEN THE GROUP OPENS, which is the only thing left to click to close it again —
  // and it is what makes the members read as the inside of something rather than as a handful of
  // nodes that appeared. Drawn round where they actually landed, so it is honest about them: in
  // the lane layouts their shared sort key puts them in a run, in the ring they may be scattered
  // and the box is correspondingly loose.
  // BOTH KINDS OF OPENED GROUP. A company bucket folds away its own node when it opens, so with
  // no box there is nothing left on the picture to close it with — the reader is stuck inside it.
  const hulls = [...opened, ...openedCompanies]
    .map((label) => {
      const at = (folded.inside.get(label) ?? []).map((m) => placed.byId.get(m)).filter((n) => n != null);
      if (at.length === 0) return null;
      const pad = 12;
      // THE LABELS, NOT THE DOTS. A node is a dot and a name, the name is the wide part, and a box
      // drawn round the dots alone crosses out every name in the group — which is what it looked
      // like: four members listed down the outside of their own box.
      const xs = at.flatMap((n) => [n.x, n.labelEnd.x]);
      const ys = at.flatMap((n) => [n.y, n.labelEnd.y]);
      const x = Math.min(...xs) - pad;
      const y = Math.min(...ys) - pad - TITLE;
      // THE NAME, NOT THE ID. A company bucket is keyed out of the thing namespace, and the prefix
      // that does that is machinery — written along the top edge it reads as part of the name.
      const name = groupName(label);
      return {
        label,
        name,
        x,
        y,
        // Wide enough for the group's OWN name too: it is written along the top edge, and a title
        // that runs past the corner reads as belonging to whatever it ends over.
        width: Math.max(Math.max(...xs) - x + pad, name.length * PER_CHAR + 2 * pad),
        height: Math.max(...ys) - y + pad,
      };
    })
    .filter((it) => it != null);

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 p-4 sm:p-6 lg:flex-row">
      {/* THE GRAPH. Scrolls in its own box, so the page itself never does. */}
      {/* `min-w-0` IS NOT DECORATION. A flex item defaults to `min-width: auto`, which means it
          refuses to shrink below its content — and the content here is a canvas nine thousand
          pixels wide. Without it the section grows to fit the drawing, the page scrolls instead of
          the picture, and the controls slide off the screen with it. */}
      <section className="box bg-surface flex min-h-0 min-w-0 flex-1 flex-col">
        {/* THREE GROUPS, NOT TEN CONTROLS IN A ROW. What the graph IS, how it is DRAWN, and what
            can be DONE to it are three different questions, and run together at one weight a
            reader has to read every label to find the one they want. Divided, and the redundant
            words dropped: a pill reading "Spalte" does not also need to be labelled "As". */}
        <h2 className="border-subtle flex flex-wrap items-center gap-x-4 gap-y-2 border-b px-4 py-2.5 text-smycles-body text-ink-muted">
          {/* WHAT IT IS. The counts are of what is DRAWN, not what the file holds: a filtered
              picture whose header counted the whole corpus describes something not on screen. */}
          <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0">
            <span className="uppercase tracking-[0.18em] text-ink">
              <FormattedMessage id="companyGraph.title" defaultMessage="Company graph" />
            </span>
            <span className="tabular-nums opacity-70">
              <FormattedMessage
                id="companyGraph.size"
                defaultMessage="{companies} companies · {nodes} things · {edges} relations"
                values={{ companies: shown.companies.length, nodes: shown.nodes.length, edges: shown.edges.length }}
              />
            </span>
          </span>

          {/* HOW IT IS DRAWN. Nothing here changes the graph; every one of them is a view. */}
          <span className="border-subtle flex flex-wrap items-center gap-x-3 gap-y-2 border-l pl-4">
            {/* Offered where the fragmentation is visible — a hundred single-thing nodes is a
                thing a reader SEES here and nowhere else. BOTH WAYS, so a reader who went too far
                is one click from back, and the control says where they are. */}
            <span className="flex items-center gap-1.5">
              <button
                type="button"
                disabled={grouping || level === 0}
                onClick={() => setLevel(level - 1)}
                aria-label={intl.formatMessage({ id: "companyGraph.finer", defaultMessage: "One level finer" })}
                className="border-subtle h-5 w-5 rounded border leading-none disabled:opacity-40"
              >
                −
              </button>
              <span className="tabular-nums">
                {grouping ? (
                  <FormattedMessage id="companyGraph.grouping" defaultMessage="Finding kinds…" />
                ) : (
                  <FormattedMessage id="companyGraph.level" defaultMessage="Level {level}" values={{ level }} />
                )}
              </span>
              <button
                type="button"
                disabled={grouping}
                onClick={() => void deeper()}
                aria-label={intl.formatMessage({ id: "companyGraph.coarser", defaultMessage: "One level coarser" })}
                className="border-subtle h-5 w-5 rounded border leading-none disabled:opacity-40"
              >
                +
              </button>
            </span>
            {/* THE COMPANY SIDE, which nothing else folds. Its values are numbers, so this one
                keeps its word: "30" alone says nothing about what thirty of. */}
            <label className="flex items-center gap-2">
              <FormattedMessage id="companyGraph.companies" defaultMessage="Companies" />
              <Select
                id="company-graph-buckets"
                variant="pill"
                hideEmpty
                value={String(buckets)}
                onChange={(it) => {
                  setBuckets(Number(it));
                  setOpenedCompanies(new Set());
                }}
                options={[
                  { value: "0", label: intl.formatMessage({ id: "companyGraph.eachCompany", defaultMessage: "each one" }) },
                  { value: "12", label: "12" },
                  { value: "30", label: "30" },
                  { value: "60", label: "60" },
                ]}
              />
            </label>
            <Select
              id="company-graph-layout"
              variant="pill"
              hideEmpty
              ariaLabel={intl.formatMessage({ id: "companyGraph.layout", defaultMessage: "As" })}
              value={kind}
              onChange={(next) => setKind(next as LayoutKind)}
              options={LAYOUTS.map((it) => ({
                value: it,
                label: intl.formatMessage({ id: `companyGraph.layout.${it}`, defaultMessage: it }),
              }))}
            />
            {/* WHICH SIDE LEADS. The drawing is a cascade — one side is placed and the other sits
                at the average position of what it touches — so an ordering is not a second pass
                over it, only a choice of which end a reader names. */}
            <Select
              id="company-graph-order"
              variant="pill"
              hideEmpty
              ariaLabel={intl.formatMessage({ id: "companyGraph.order", defaultMessage: "Sorted" })}
              value={order}
              onChange={(next) => setOrder(next as Order)}
              options={ORDERS.map((it) => ({
                value: it,
                label: intl.formatMessage({ id: `companyGraph.order.${it}`, defaultMessage: it }),
              }))}
            />
            {/* THE WAY BACK. A click zooms in, so without this a reader who clicked once has no
                way out of it, and the gesture reads as a trap rather than a tool. */}
            <span className="flex items-center gap-1.5">
              <button
                type="button"
                disabled={zoom <= furthest()}
                onClick={() => zoomBy(1 / STEP)}
                aria-label={intl.formatMessage({ id: "companyGraph.zoomOut", defaultMessage: "Further away" })}
                className="border-subtle h-5 w-5 rounded border leading-none disabled:opacity-40"
              >
                −
              </button>
              {/* ONE CLICK BACK TO THE WHOLE THING. Stepping out by halves from eight hundred per
                  cent is nine clicks, and a reader who has lost the picture wants it back. */}
              <button
                type="button"
                onClick={() => setZoom(Math.min(1, fitScale() ?? 1))}
                aria-label={intl.formatMessage({ id: "companyGraph.zoomFit", defaultMessage: "Show the whole picture" })}
                className="tabular-nums underline"
              >
                <FormattedMessage id="companyGraph.zoom" defaultMessage="{percent}%" values={{ percent: Math.round(zoom * 100) }} />
              </button>
              <button
                type="button"
                disabled={zoom >= CLOSEST}
                onClick={() => zoomBy(STEP)}
                aria-label={intl.formatMessage({ id: "companyGraph.zoomIn", defaultMessage: "Closer in" })}
                className="border-subtle h-5 w-5 rounded border leading-none disabled:opacity-40"
              >
                +
              </button>
            </span>
            {/* ONE WAY OUT OF ALL FOUR. Four filters mean four ways to end up looking at
                nothing, and a reader who has lost the picture wants it back, not four controls
                to find. Shown only while there is something to clear. */}
            {(only.size > 0 || onlyThings.size > 0 || onlyRelations.size > 0 || onlySources.size > 0) && (
              <button
                type="button"
                className="underline"
                onClick={() => {
                  setOnly(new Set());
                  setOnlyThings(new Set());
                  setOnlyRelations(new Set());
                  setOnlySources(new Set());
                }}
              >
                <FormattedMessage id="companyGraph.resetFilter" defaultMessage="Clear the filter" />
              </button>
            )}
          </span>

          {/* WHICH GRAPH, AND WHAT BECOMES OF IT. Pushed to its own end because these are the only
              controls here that outlive the page: one of them decides what every question is
              answered from, and one of them cannot be undone. */}
          <span className="border-subtle ml-auto flex flex-wrap items-center gap-x-3 gap-y-2 border-l pl-4">
            {/* WHICH ONE IS DRAWN, not which one is answered from. The runs beside the drawing are
                the walks made on the active one, so looking at another shows an empty list. */}
            {catalogue.length > 1 && (
              <Select
                id="company-graph-pick"
                variant="pill"
                hideEmpty
                ariaLabel={intl.formatMessage({ id: "companyGraph.showing", defaultMessage: "Showing" })}
                value={graph.id ?? catalogue[0]?.id}
                /* A GROUPING RUN ENDS BY RE-READING THE GRAPH IT WAS STARTED ON. Switch away
                   while it runs and that re-read lands on the page showing another graph, which
                   then draws the OLD one under the NEW name with nothing saying so. */
                disabled={grouping}
                onChange={(id) => setViewing(id)}
                options={catalogue.map((it) => ({ value: it.id, label: it.name }))}
              />
            )}
            {/* THE ONE EVERY READER GETS. Said here rather than only in a list, because the page a
                graph is read on is where someone decides it is the one worth answering from. The
                sentence it used to spell out is on the control, where a reader who wants it looks
                and a reader who already knows is not made to read it again. */}
            {isActive ? (
              <span
                className="text-ink"
                title={intl.formatMessage({ id: "companyGraph.isActiveWhy", defaultMessage: "Every question goes to this graph" })}
              >
                <FormattedMessage id="companyGraph.isActive" defaultMessage="Active" />
              </span>
            ) : (
              <button
                type="button"
                className="underline"
                disabled={activating}
                title={intl.formatMessage({ id: "companyGraph.activateWhy", defaultMessage: "Answer every question from this one" })}
                onClick={() => void activate()}
              >
                <FormattedMessage id="companyGraph.activate" defaultMessage="Make active" />
              </button>
            )}
            {/* TWO CLICKS, because there is no undo and no copy kept. Offered only for a graph this
                instance can actually remove: the built-in ships in the image, the service refuses
                it, and a control that asks for something it will not get is worse than none. */}
            {deletable &&
              (asked ? (
                <span className="flex items-center gap-2">
                  <FormattedMessage id="companyGraph.deleteAsk" defaultMessage="Delete {name}?" values={{ name: graph.name ?? graph.id }} />
                  <button type="button" className="underline" disabled={deleting} onClick={() => void remove()}>
                    <FormattedMessage id="companyGraph.deleteYes" defaultMessage="Delete" />
                  </button>
                  <button type="button" className="underline" onClick={() => setAsking(null)}>
                    <FormattedMessage id="companyGraph.deleteNo" defaultMessage="Keep it" />
                  </button>
                </span>
              ) : (
                <button type="button" className="underline" onClick={() => setAsking(graph.id ?? null)}>
                  <FormattedMessage id="companyGraph.delete" defaultMessage="Delete" />
                </button>
              ))}
            {/* The one place a reader is already looking at graphs is the place to offer another. */}
            <Link to="/admin/company-graph/build" className="underline">
              <FormattedMessage id="companyGraph.build" defaultMessage="Build one…" />
            </Link>
            {problem !== null && (
              <span role="status" className="text-danger">
                {problem === "activate" && (
                  <FormattedMessage
                    id="companyGraph.activateFailed"
                    defaultMessage="This graph could not be made active. Nothing was changed."
                  />
                )}
                {problem === "delete" && (
                  <FormattedMessage
                    id="companyGraph.deleteFailed"
                    defaultMessage="This graph could not be deleted. Nothing was removed."
                  />
                )}
                {problem === "list" && (
                  <FormattedMessage
                    id="companyGraph.listFailed"
                    defaultMessage="The list of graphs could not be read, so activating and deleting are unavailable."
                  />
                )}
                {problem === "grouping" && (
                  <FormattedMessage
                    id="companyGraph.groupingLost"
                    defaultMessage="The grouping run is taking too long to follow. It may still finish – reload the page to see where it got to."
                  />
                )}
              </span>
            )}
          </span>
        </h2>
        <div ref={attach} className="min-h-0 min-w-0 flex-1 overflow-auto p-2">
          <svg
            viewBox={`0 0 ${placed.width} ${placed.height}`}
            width={placed.width * zoom}
            height={placed.height * zoom}
            role="img"
            aria-label={intl.formatMessage({
              id: "companyGraph.figure",
              defaultMessage:
                "A node-link diagram: the things companies relate to on an outer ring, the companies on an inner ring, joined by one chord per relation.",
            })}
            className="max-w-none text-ink"
            onClick={(event) => {
              // A CLICK ON THE PICTURE IS A STEP INTO IT. Nodes and group boxes stop the event,
              // so this is only ever the ground between them.
              setHovered(null);
              zoomAbout(event.clientX, event.clientY, STEP);
            }}
          >
            <defs>
              {Object.keys(RELATION_COLOUR).map((predicate) => (
                <marker
                  key={predicate}
                  id={`cg-${predicate}`}
                  viewBox="0 -4 8 8"
                  refX={7.5}
                  refY={0}
                  markerWidth={4.6}
                  markerHeight={4.6}
                  orient="auto"
                >
                  <path d="M0,-3L7.5,0L0,3" fill={colourOf(predicate)} />
                </marker>
              ))}
            </defs>

            {/* The group labels, so the outer ring reads as grouped rather than arbitrary. */}
            {placed.arcs.map((arc) => {
              const mid = (arc.a0 + arc.a1) / 2;
              return (
                <text
                  key={arc.key}
                  x={placed.cx + Math.cos(mid) * 456}
                  y={placed.cy + Math.sin(mid) * 456}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  stroke="var(--color-surface)"
                  strokeWidth={3}
                  paintOrder="stroke fill"
                  className="fill-current text-smycles-micro uppercase tracking-[0.1em] opacity-40"
                >
                  {arc.key}
                </text>
              );
            })}

            {/* THE OPENED GROUPS, UNDER THE EDGES. A panel over them would hide the group's own
                lines, and which member a company reaches is the whole reason to open it; an opaque
                panel under them hides nothing, because its fill is the page's own ground. What
                makes the members legible is the room reserved either side of the run and the rest
                of the picture going quiet, not a lid. Clicking it folds it back. */}
            {hulls.map((hull) => (
              <g
                key={`hull-${hull.label}`}
                className="cursor-pointer"
                onMouseEnter={() => setHovered(hull.label)}
                onMouseLeave={() => setHovered(null)}
                onClick={(event) => {
                event.stopPropagation();
                const close = (before: ReadonlySet<string>) => {
                  const next = new Set(before);
                  next.delete(hull.label);
                  return next;
                };
                if (openedCompanies.has(hull.label)) setOpenedCompanies(close);
                else setOpened(close);
              }}>
                <rect
                  x={hull.x}
                  y={hull.y}
                  width={hull.width}
                  height={hull.height}
                  rx={6}
                  className="fill-current stroke-current"
                  fillOpacity={0.05}
                  strokeOpacity={0.5}
                  strokeWidth={1}
                />
                <text
                  x={hull.x + 12}
                  y={hull.y + TITLE}
                  className="fill-current text-smycles-label font-semibold opacity-60"
                  stroke="var(--color-surface)"
                  strokeWidth={3}
                  paintOrder="stroke fill"
                >
                  {hull.name}
                </text>
              </g>
            ))}

            {/* Edges first, so no label is drawn under a chord. */}
            {placed.edges.map((edge) => {
              const on = isLit(edge);
              return (
                <path
                  key={edge.id}
                  className="cg-edge"
                  d={edge.d}
                  fill="none"
                  stroke={washed && !dimmed ? PLAIN : colourOf(edge.predicate)}
                  // THICKER FOR MORE. A company reaching four of a group's things has a different
                  // relationship to it than one reaching a single thing, and the line is the only
                  // place that difference can be seen once the members are folded away. Capped,
                  // because a group of forty would otherwise draw a bar.
                  strokeWidth={Math.max(0.4, ((edge.ternary ? 1.4 : 1.5) + Math.min(edge.weight - 1, 6) * 0.55) / crowd)}
                  // An ENDED relation is dashed and a ternary supply arrow is too — both are things
                  // the edge says that its colour cannot.
                  strokeDasharray={edge.ternary ? "4 3" : edge.status === "ENDED" ? "6 4" : undefined}
                  markerEnd={on ? `url(#cg-${edge.predicate})` : undefined}
                  // Dimmed rather than hidden: the unlit edges are what give the lit ones somewhere
                  // to be, and without them the view shows a few lines floating in blank space.
                  opacity={dimmed ? (on ? 0.86 : 0.06) : atRest}
                >
                  <title>{`${edge.from.id} — ${edge.predicate} → ${edge.to.id}${edge.evidence ? ` (${edge.evidence})` : ""}`}</title>
                </path>
              );
            })}

            {placed.nodes.map((node) => {
              const on = nodeLit(node.id);
              // A COMPANY LABEL POINTS INWARD, across the chord bundle, so a long one is written
              // over the busiest part of the picture. Cut here; the whole name is in the tooltip.
              // ONLY A COMPANY. A group's name went through the same cut because it was not a
              // thing — so `Medizinische Versorgungskonzepte` was drawn as `Medizinische Versorgu…`
              // inside a box sized for the whole name, and nothing could find it by the name it
              // had. A group name is not a legal entity: it has no `GmbH` to strip and no reason
              // to be shortened.
              // A BUCKET IS NOT A LEGAL ENTITY. It has no `GmbH` to strip, and its name is already
              // short — the cut is for the company names that run across the chord bundle.
              const isCompanyGroup = folded.companyGroups.has(node.id);
              // HOW MANY IT STANDS FOR. The card hides a count the reader could previously read off
              // the ring by eye, and "Kliniken" over fourteen companies is a different fact from
              // "Kliniken" over two.
              const held = folded.companyMembers.get(node.id)?.length ?? 0;
              const label = isCompanyGroup
                ? `${groupName(node.id)} · ${held}`
                : node.kind !== "company"
                  ? node.id
                  : shortName(node.id).length > COMPANY_LABEL
                    ? `${shortName(node.id).slice(0, COMPANY_LABEL - 1)}…`
                    : shortName(node.id);
              // NULL IS THE CIRCLE, and the circle is a summary thing — see `markPoints`.
              const mark = node.kind === "thing"
                ? markPoints(sourceOf.get(node.id) ?? "summary", radiusOf(node.degree))
                : null;
              return (
                <g
                  key={node.id}
                  transform={`translate(${node.x},${node.y})`}
                  opacity={on ? 1 : 0.13}
                  className="cursor-pointer"
                  onMouseEnter={() => setHovered(node.id)}
                  onMouseLeave={() => setHovered(null)}
                  onClick={(event) => {
                    event.stopPropagation();
                    setHovered(node.id);
                  }}
                  // DOUBLE-CLICK OPENS THE COMPANY, single click still pins its neighbourhood —
                  // the two gestures would otherwise fight over the one thing a reader does most.
                  // A node whose name is in no company the corpus holds simply does not open.
                  // DOUBLE-CLICK IS THE NODE'S OWN ACTION — a company opens its page, a group
                  // opens into its members. Single click still pins a neighbourhood everywhere,
                  // so the two gestures never fight over the one thing a reader does most.
                  onDoubleClick={() => {
                    if (isCompanyGroup || openedCompanies.has(node.id)) {
                      setOpenedCompanies((before) => {
                        const next = new Set(before);
                        if (!next.delete(node.id)) next.add(node.id);
                        return next;
                      });
                      return;
                    }
                    if (node.kind === "cluster" || opened.has(node.id)) {
                      setOpened((before) => {
                        const next = new Set(before);
                        if (!next.delete(node.id)) next.add(node.id);
                        return next;
                      });
                      return;
                    }
                    const company = node.kind === "company" ? idOf.get(node.id) : undefined;
                    if (company) navigate(`/c/${company}`);
                  }}
                >
                  {isCompanyGroup ? (
                    // A STACK OF CARDS, because it stands for legal entities and the single ones
                    // beside it are solid squares. The sheet behind is the whole mark: a reader
                    // tells a pile from a place without reading either label.
                    <>
                      <rect
                        x={-(label.length * PER_CHAR) / 2 - 3}
                        y={-7}
                        width={label.length * PER_CHAR + 12}
                        height={20}
                        rx={3}
                        className="fill-current"
                        fillOpacity={0.1}
                      />
                      <rect
                        x={-(label.length * PER_CHAR) / 2 - 6}
                        y={-10}
                        width={label.length * PER_CHAR + 12}
                        height={20}
                        rx={3}
                        className="fill-current stroke-current"
                        fillOpacity={0.2}
                        strokeWidth={1}
                      />
                    </>
                  ) : node.kind === "cluster" ? (
                    // A PILL, not a card: a thing group is a FIELD the graph talks about, and the
                    // things beside it are circles. Lighter than the stack on purpose — the outer
                    // ring carries most of the names, so its marks have to sit under them.
                    <rect
                      x={-(label.length * PER_CHAR) / 2 - 7}
                      y={-10}
                      width={label.length * PER_CHAR + 14}
                      height={20}
                      rx={10}
                      className="fill-current stroke-current"
                      fillOpacity={0.06}
                      strokeOpacity={0.4}
                      strokeWidth={1}
                    />
                  ) : node.kind === "thing" ? (
                    // A DOT, A DIAMOND OR A TRIANGLE, by the input that named this thing. The
                    // colour on this page belongs to the relation, which is a property of the
                    // LINE; a source is a property of the node, so it gets the encoding the lines
                    // cannot take. A circle is still a summary thing, so a graph built before the
                    // sources existed is drawn exactly as it was.
                    mark ? (
                      <polygon
                        points={mark}
                        className="fill-current stroke-current"
                        fillOpacity={0.16}
                        strokeWidth={1.5}
                        strokeLinejoin="round"
                      />
                    ) : (
                      <circle
                        r={radiusOf(node.degree)}
                        className="fill-current stroke-current"
                        fillOpacity={0.16}
                        strokeWidth={1.5}
                      />
                    )
                  ) : (
                    // A hollow, dashed square is a company with no edge. It is still in the graph —
                    // the universe an exclusion is taken from — so it must be drawn, not omitted.
                    <rect
                      x={-3.6}
                      y={-3.6}
                      width={7.2}
                      height={7.2}
                      className="stroke-current"
                      fill={node.edgeless ? "none" : "currentColor"}
                      strokeWidth={1.1}
                      strokeDasharray={node.edgeless ? "1.6 1.6" : undefined}
                    />
                  )}
                  <text
                    transform={node.kind === "cluster" || isCompanyGroup ? undefined : node.labelTransform}
                    textAnchor={node.kind === "cluster" || isCompanyGroup ? "middle" : node.labelAnchor}
                    dy={node.kind === "cluster" || isCompanyGroup ? 4 : undefined}
                    dominantBaseline="middle"
                    // THE HALO IS WHAT MAKES A LABEL READABLE OVER THE CHORDS. Without it every
                    // line crossing a name is drawn through the letters; `paint-order` puts the
                    // stroke behind the fill so the surface colour becomes a backing, not an
                    // outline over the text.
                    stroke="var(--color-surface)"
                    strokeWidth={3}
                    strokeLinejoin="round"
                    paintOrder="stroke fill"
                    className={`pointer-events-none fill-current ${
                      node.kind === "thing" ? "text-smycles-label font-semibold" : "text-smycles-micro opacity-70"
                    }`}
                  >
                    {label}
                    <title>
                      {node.id}
                      {node.kind === "company" && idOf.has(node.id)
                        ? ` — ${intl.formatMessage({ id: "companyGraph.openHint", defaultMessage: "double-click to open" })}`
                        : ""}
                    </title>
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </section>

      {/* ONE PANEL, FIVE SECTIONS. Each list keeps its own scroll — they are five lists and one
          shared scroll would mean hunting for the relations under three thousand things — but the
          chrome between them is a rule rather than a box, a border, a radius and a bar. */}
      <div
        className="box bg-surface relative flex min-h-0 w-full flex-col lg:w-[var(--side-w)] lg:shrink-0"
        style={{ "--side-w": `${sideWidth}px` } as React.CSSProperties}
      >
        {/* THE GRIP, in the gap between the picture and the panel. No ARIA role: a real
            `separator` owes tabIndex, aria-valuemin/max/now and arrow-key resizing to be worth
            announcing, and this is a power-user nicety beside a page that works without it —
            the same call `AssistantDrawer` makes for its own edge. */}
        <div
          data-testid="company-graph-grip"
          onPointerDown={onGripDown}
          onPointerMove={onGripMove}
          onPointerUp={onGripUp}
          aria-hidden
          className="absolute inset-y-0 left-0 z-10 hidden w-2 -translate-x-1/2 cursor-ew-resize touch-none hover:bg-ink/[0.08] lg:block"
        />
        {/* THE FILTER. A view setting: it changes what is drawn and nothing about what a question
            is answered from, which is why it lives beside the picture and not in the agent store. */}
        <section
          data-testid="company-graph-list-0"
          ref={(it) => { panes.current[0] = it; }}
          style={{ flexGrow: shares[0] ?? 0, flexShrink: 1, flexBasis: shares[0] == null ? "auto" : 0 }}
          className="border-subtle relative flex min-h-0 flex-col border-b"
        >
          {/* THE RULE IS THE GRIP. Dragging it moves height from this list to the one below, and
              from nothing else: the pane's weight and its neighbour's are traded, so the third
              keeps the share it had. */}
          <div
            data-testid="company-graph-pane-0"
            onPointerDown={onPaneDown(0)}
            onPointerMove={onPaneMove}
            onPointerUp={onPaneUp}
            aria-hidden
            className="absolute inset-x-0 bottom-0 z-10 h-2 translate-y-1/2 cursor-ns-resize touch-none hover:bg-ink/[0.08]"
          />
          <PanelHead shown={companiesShown.length} total={graph.companies.length}>
            <FormattedMessage id="companyGraph.only" defaultMessage="Companies" />
          </PanelHead>
          <div className="px-2 pt-2">
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={intl.formatMessage({ id: "companyGraph.onlyFind", defaultMessage: "Find a company" })}
              className={FIND}
            />
          </div>
          <ul className="min-h-0 flex-1 overflow-y-auto p-2">
            {companiesShown.map((company) => (
              <li key={company}>
                <label className={ROW}>
                  <input
                    type="checkbox"
                    checked={only.has(company)}
                    onChange={() =>
                      setOnly((before) => {
                        const next = new Set(before);
                        if (!next.delete(company)) next.add(company);
                        return next;
                      })
                    }
                  />
                  <span className="min-w-0 flex-1 truncate text-smycles-body text-ink">{shortName(company)}</span>
                </label>
              </li>
            ))}
          </ul>
        </section>
        {/* THE SAME FOR THE THINGS. Two filters that meet in the middle: a reader can ask "these
            companies", "these things", or "these companies, and only where they touch these
            things" without the control knowing it is answering three questions. */}
        <section
          data-testid="company-graph-list-1"
          ref={(it) => { panes.current[1] = it; }}
          style={{ flexGrow: shares[1] ?? 0, flexShrink: 1, flexBasis: shares[1] == null ? "auto" : 0 }}
          className="border-subtle relative flex min-h-0 flex-col border-b"
        >
          {/* THE RULE IS THE GRIP. Dragging it moves height from this list to the one below, and
              from nothing else: the pane's weight and its neighbour's are traded, so the rest
              keep the share they had. */}
          <div
            data-testid="company-graph-pane-1"
            onPointerDown={onPaneDown(1)}
            onPointerMove={onPaneMove}
            onPointerUp={onPaneUp}
            aria-hidden
            className="absolute inset-x-0 bottom-0 z-10 h-2 translate-y-1/2 cursor-ns-resize touch-none hover:bg-ink/[0.08]"
          />
          <PanelHead shown={thingsShown.length} total={pickable.length}>
            <FormattedMessage id="companyGraph.onlyThings" defaultMessage="Things" />
          </PanelHead>
          <div className="px-2 pt-2">
            <input
              type="search"
              value={searchThings}
              onChange={(event) => setSearchThings(event.target.value)}
              placeholder={intl.formatMessage({ id: "companyGraph.onlyFindThing", defaultMessage: "Find a thing" })}
              className={FIND}
            />
          </div>
          <ul className="min-h-0 flex-1 overflow-y-auto p-2">
            {/* THE GROUPS FIRST, with what each one covers. A group is a handle on a dozen things,
                not a thirteenth thing, and the count is what says so — picking one picks its
                leaves, because a group is never an edge's object. */}
            {thingsShown.map((it) => (
              <li key={it.label}>
                <label className={ROW}>
                  <input
                    type="checkbox"
                    checked={onlyThings.has(it.label)}
                    onChange={() =>
                      setOnlyThings((before) => {
                        const next = new Set(before);
                        if (!next.delete(it.label)) next.add(it.label);
                        return next;
                      })
                    }
                  />
                  <span className={`min-w-0 flex-1 truncate text-smycles-body text-ink${it.covers ? " font-medium" : ""}`}>
                    {it.label}
                  </span>
                  {it.covers != null && (
                    <span className="shrink-0 tabular-nums text-smycles-caption text-ink-muted">{it.covers}</span>
                  )}
                </label>
              </li>
            ))}
          </ul>
        </section>
        {/* THE RUNS. Newest first, which is the order a reader thinks in — the question just asked
            is the one they want to see drawn. */}
        <section
          data-testid="company-graph-list-2"
          ref={(it) => { panes.current[2] = it; }}
          style={{ flexGrow: shares[2] ?? 0, flexShrink: 1, flexBasis: shares[2] == null ? "auto" : 0 }}
          className="border-subtle relative flex min-h-0 flex-col border-b"
        >
          {/* The rule above the relations. It was the one separator with nothing on it, because the
              list below sizes to its own content — which is right until a reader wants the searches
              taller and the eight relation rows scrolled instead. */}
          <div
            data-testid="company-graph-pane-2"
            onPointerDown={onPaneDown(2)}
            onPointerMove={onPaneMove}
            onPointerUp={onPaneUp}
            aria-hidden
            className="absolute inset-x-0 bottom-0 z-10 h-2 translate-y-1/2 cursor-ns-resize touch-none hover:bg-ink/[0.08]"
          />
          <PanelHead shown={runs.length}>
            <FormattedMessage id="companyGraph.runs" defaultMessage="Recent graph searches" />
          </PanelHead>
          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            {runs.length === 0 && (
              <p className="px-2 py-3 text-smycles-body text-ink-muted">
                <FormattedMessage
                  id="companyGraph.noRuns"
                  defaultMessage="No graph searches yet. Ask about companies: the questions answered from this graph land here."
                />
              </p>
            )}
            {runs.map((entry) => (
              <button
                key={keyOf(entry)}
                type="button"
                aria-pressed={selected === keyOf(entry)}
                onClick={() => setSelected(selected === keyOf(entry) ? null : keyOf(entry))}
                // A RULE DOWN THE EDGE, not a tint alone. Four of these stacked at eight per cent
                // read as four cards, and which one is drawn on the canvas is the one fact this
                // list has to be able to state at a glance.
                className={`mb-0.5 block w-full border-l-2 py-1.5 pl-2.5 pr-2 text-left transition ${
                  selected === keyOf(entry) ? "border-ink bg-ink/[0.06]" : "border-transparent hover:bg-ink/[0.04]"
                }`}
              >
                <span className="block truncate text-smycles-body text-ink">{entry.question}</span>
                {/* ONE META LINE. Time, what was asked for, and what it found were three wrapped
                    rows in a 300px column — the question itself was the smallest part of its own
                    entry. */}
                <span className="mt-0.5 flex items-baseline gap-x-2 text-smycles-caption text-ink-muted">
                  <span className="shrink-0 tabular-nums">{timeOf(entry.at)}</span>
                  <span className="min-w-0 flex-1 truncate">
                    {entry.demands.map((demand, at) => (
                      <span key={`${demand.thing}-${at}`} className={demand.resolved ? "" : "line-through"}>
                        {at > 0 && ", "}
                        {demand.relation ?? intl.formatMessage({ id: "companyGraph.anyRelation", defaultMessage: "any" })}
                        {" · "}
                        {demand.thing}
                      </span>
                    ))}
                  </span>
                  <span className="shrink-0 tabular-nums opacity-80">
                    <FormattedMessage
                      id="companyGraph.edgeCount"
                      defaultMessage="{edges} edges · {companies} companies"
                      values={{ edges: entry.edges.length, companies: entry.companies.length }}
                    />
                  </span>
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* THE KEY AND THE THIRD FILTER, in one list and in the shape of the other two. It used
            to be a row of buttons that dimmed an edge to six per cent — which reads as a filter
            and is not one: the companies and the things stayed, so asking for SUPPLIES left a
            hundred and fifty names around six visible lines. */}
        <section
          data-testid="company-graph-list-3"
          ref={(it) => { panes.current[3] = it; }}
          style={{ flexGrow: shares[3] ?? 0, flexShrink: 1, flexBasis: shares[3] == null ? "auto" : 0 }}
          className="border-subtle relative flex min-h-0 flex-col border-b"
        >
          {/* The rule above the sources, for the same reason the one above the relations exists:
              both lists size to their own content, which is right until a reader wants one of them
              scrolled so the searches can be taller. */}
          <div
            data-testid="company-graph-pane-3"
            onPointerDown={onPaneDown(3)}
            onPointerMove={onPaneMove}
            onPointerUp={onPaneUp}
            aria-hidden
            className="absolute inset-x-0 bottom-0 z-10 h-2 translate-y-1/2 cursor-ns-resize touch-none hover:bg-ink/[0.08]"
          />
          <PanelHead shown={relationsIn.length}>
            <FormattedMessage id="companyGraph.relation" defaultMessage="Relation" />
          </PanelHead>
          <ul className="min-h-0 overflow-y-auto p-2">
            {/* FROM THE WHOLE GRAPH, not from what is drawn. Listing only the relations still on
                the picture would take the last one off the list the moment it was picked, and
                there would be no way back. */}
            {relationsIn.map((predicate) => (
              <li key={predicate}>
                <label className={ROW}>
                  <input
                    type="checkbox"
                    checked={onlyRelations.has(predicate)}
                    onChange={() =>
                      setOnlyRelations((before) => {
                        const next = new Set(before);
                        if (!next.delete(predicate)) next.add(predicate);
                        return next;
                      })
                    }
                  />
                  {/* A FILLED CHIP, not a two-pixel rule. The colour is the only thing tying this
                      row to the lines on the canvas, and a hairline of a mid-tone hue is the one
                      thing on this page nobody could read. */}
                  <span
                    aria-hidden
                    className="border-subtle h-4 w-5 shrink-0 rounded-sm border"
                    style={{ background: colourOf(predicate) }}
                  />
                  <span className="min-w-0 flex-1 truncate text-smycles-body text-ink">{relationName(predicate)}</span>
                  <span className="shrink-0 tabular-nums text-smycles-caption text-ink-muted">
                    {relationCounts.get(predicate) ?? 0}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </section>

        {/* WHERE THE THINGS CAME FROM — the legend for the shapes on the picture, which is also
            the filter for them. Kept as one control because a legend a reader cannot act on sends
            them hunting for the filter that goes with it, and a filter with no key makes them
            guess what a diamond meant. */}
        <section
          data-testid="company-graph-list-4"
          ref={(it) => { panes.current[4] = it; }}
          style={{ flexGrow: shares[4] ?? 0, flexShrink: 1, flexBasis: shares[4] == null ? "auto" : 0 }}
          className="relative flex min-h-0 flex-col"
        >
          <PanelHead shown={sourcesIn.length}>
            <FormattedMessage id="companyGraph.source" defaultMessage="Source" />
          </PanelHead>
          <ul className="min-h-0 overflow-y-auto p-2">
            {/* FROM THE WHOLE GRAPH, like the relations above and for the same reason: listing
                only the sources still drawn would take the last one off the list the moment it
                was picked. */}
            {sourcesIn.map((source) => (
              <li key={source}>
                <label className={ROW}>
                  <input
                    type="checkbox"
                    checked={onlySources.has(source)}
                    onChange={() =>
                      setOnlySources((before) => {
                        const next = new Set(before);
                        if (!next.delete(source)) next.add(source);
                        return next;
                      })
                    }
                  />
                  {/* THE MARK ITSELF, drawn by the same function the canvas draws it with — a
                      hand-copied glyph here is the legend and the picture able to disagree. */}
                  <svg viewBox="-9 -9 18 18" aria-hidden className="h-4 w-5 shrink-0 text-ink">
                    {markPoints(source, 6) ? (
                      <polygon
                        points={markPoints(source, 6) ?? ""}
                        className="fill-current stroke-current"
                        fillOpacity={0.16}
                        strokeWidth={1.5}
                        strokeLinejoin="round"
                      />
                    ) : (
                      <circle r={6} className="fill-current stroke-current" fillOpacity={0.16} strokeWidth={1.5} />
                    )}
                  </svg>
                  <span className="min-w-0 flex-1 truncate text-smycles-body text-ink">{sourceName(source)}</span>
                  <span className="shrink-0 tabular-nums text-smycles-caption text-ink-muted">
                    {sourceCounts.get(source) ?? 0}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
